// src/hooks/useHomePageState.js
import { useState, useEffect, useMemo, useRef } from 'react';
import { useSupabaseData } from '@/hooks/useSupabaseData';
import { formatToYYYYMMDD } from '@/lib/dateUtils';
import { buildTeacherSlug, parseTeacherSlug, buildGroupSlug } from '@/lib/slug';
import { useToast } from '@/components/ui/use-toast';

export const ViewModes = {
  BY_DATE: 'by_date',
  BY_GROUP: 'by_group',
  BY_TEACHER: 'by_teacher',
};
export const TeacherViewScopes = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
};

export function useHomePageState(initialMode, slug, location) {
  const { toast } = useToast();


  // date
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  useEffect(() => {
    const stored = localStorage.getItem('selectedDate');
    if (stored) {
      const parsed = new Date(stored);
      if (!isNaN(parsed)) setSelectedDate(parsed);
    }
  }, []);
  const handleDateChange = (newDate) => {
    if (!newDate) return;
    setSelectedDate(newDate);
  };
  const dateStr = formatToYYYYMMDD(selectedDate);
  const currentYear = selectedDate.getFullYear();


// ✅ Межі тижня (пн–пт) у ЛОКАЛЬНОМУ часі (без UTC-зсуву)
  const weekRange = useMemo(() => {
    const d = new Date(selectedDate);
    const day = d.getDay(); // 0..6 (нд..сб)
    const diffToMon = (day + 6) % 7; // відкат до понеділка
    const monday = new Date(d);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(d.getDate() - diffToMon);
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    friday.setHours(23, 59, 59, 999);

    const ymdLocal = (dt) => {
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const dd = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    };
    return {
      monday,
      friday,
      mondayStr: ymdLocal(monday),
      fridayStr: ymdLocal(friday),
    };
  }, [selectedDate]);



  // ui state
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [viewMode, setViewMode] = useState(
    initialMode === 'by_teacher'
      ? ViewModes.BY_TEACHER
      : initialMode === 'by_group'
      ? ViewModes.BY_GROUP
      : ViewModes.BY_DATE
  );
  const [teacherViewScope, setTeacherViewScope] = useState(TeacherViewScopes.DAY);
  const [selectedTeacherGroup, setSelectedTeacherGroup] = useState('');
  const [selectedTeacherMonth, setSelectedTeacherMonth] = useState('');
  const [localLoading, setLocalLoading] = useState(true);
  const [showTools, setShowTools] = useState(false);
  const [clickCount, setClickCount] = useState(0);

  // sync mode with url
  useEffect(() => {
    const path = location?.pathname || '';
    if (path.startsWith('/date')) setViewMode(ViewModes.BY_DATE);
    else if (path.startsWith('/group')) setViewMode(ViewModes.BY_GROUP);
    else if (path.startsWith('/teacher')) setViewMode(ViewModes.BY_TEACHER);
  }, [location?.pathname]);

  // clear selection on root pages
  useEffect(() => {
    if (location?.pathname === '/group' && selectedGroup) setSelectedGroup('');
    if (location?.pathname === '/teacher' && selectedTeacher) setSelectedTeacher('');
  }, [location?.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // reference data
  const {
    data: teacherAssignments = [],
    loading: assignmentsLoading,
    refreshData: refreshAssignments,
  } = useSupabaseData('teacher_assignments', [], true);

  const allGroups = useMemo(
    () =>
      [...new Set(teacherAssignments.map((ta) => ta.group_name).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'uk', { sensitivity: 'base' })
      ),
    [teacherAssignments]
  );
  const allTeachers = useMemo(
    () =>
      [...new Set(teacherAssignments.map((ta) => ta.teacher).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'uk', { sensitivity: 'base' })
      ),
    [teacherAssignments]
  );

  // url → selected entities
  const urlSelectedGroup = useMemo(() => {
    if (!slug || allGroups.length === 0) return null;
    return allGroups.find((g) => buildGroupSlug(g) === String(slug).toUpperCase()) || null;
  }, [slug, allGroups]);

  const urlSelectedTeacher = useMemo(() => {
    if (viewMode !== ViewModes.BY_TEACHER || !slug || allTeachers.length === 0) return null;
    const { base, index } = parseTeacherSlug(slug);
    if (index && index >= 1 && index <= allTeachers.length) {
      const t = allTeachers[index - 1];
      const full = buildTeacherSlug(t, index);
      if (full.toLowerCase().startsWith(String(base).toLowerCase())) return t;
    }
    return (
      allTeachers.find((t, idx) =>
        buildTeacherSlug(t, idx + 1).toLowerCase().startsWith(String(base).toLowerCase())
      ) || null
    );
  }, [viewMode, slug, allTeachers]);

  // apply url selection into state (for UI)
  useEffect(() => {
    if (urlSelectedGroup && !selectedGroup) setSelectedGroup(urlSelectedGroup);
  }, [urlSelectedGroup, selectedGroup]);
  useEffect(() => {
    if (urlSelectedTeacher && !selectedTeacher) setSelectedTeacher(urlSelectedTeacher);
  }, [urlSelectedTeacher, selectedTeacher]);

  // effective selection
  const effectiveGroup = urlSelectedGroup ?? selectedGroup;
  const effectiveTeacher = urlSelectedTeacher ?? selectedTeacher;

  // schedule filter
const scheduleFilter = useMemo(() => {
    if (viewMode === ViewModes.BY_DATE) return { date: dateStr };
    if (viewMode === ViewModes.BY_GROUP) {
      return effectiveGroup ? { date: dateStr, group_name: effectiveGroup } : { date: dateStr };
    }
    if (viewMode === ViewModes.BY_TEACHER) {
      // без вибору — гарантовано 0 рядків з бекенда
      if (!effectiveTeacher) return { teacher: '__NO_SELECTION__' };
// DAY: тягнемо по даті (сервер)
      if (teacherViewScope === TeacherViewScopes.DAY)    return { date: dateStr };
      // WEEK: тягнемо по викладачу за рік (щоб був увесь тиждень), далі — клієнтський фільтр по пн–пт
      if (teacherViewScope === TeacherViewScopes.WEEK)   return { teacher: effectiveTeacher, year: currentYear };
      // MONTH: достатньо тягнути по викладачу (клієнт відфільтрує за місяцем)
      if (teacherViewScope === TeacherViewScopes.MONTH)  return { teacher: effectiveTeacher };
      // YEAR: тягнемо по викладачу за рік
        if (teacherViewScope === TeacherViewScopes.YEAR)   return { teacher: effectiveTeacher, year: currentYear };
    }
    return { date: dateStr };
  }, [viewMode, dateStr, effectiveGroup, effectiveTeacher, teacherViewScope, currentYear]);

  // load schedules
  const {
    data: schedulesRaw = [],
    loading: schedulesLoading,
    refreshData: refreshSchedules,
  } = useSupabaseData('schedules', [], true, scheduleFilter);
  
  // коли вчителя визначили (з URL або кліком), підтягуємо актуальне
  useEffect(() => {
    if (viewMode === ViewModes.BY_TEACHER) {
      refreshSchedules();
    }
  }, [viewMode, effectiveTeacher, teacherViewScope, dateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  // push group slug on manual selection
  useEffect(() => {
    if (!selectedGroup) return;
    const sg = buildGroupSlug(selectedGroup);
    const target = `/group/${sg}`;
    if (window.location.pathname !== target) {
      window.history.pushState(null, '', target);
    }
  }, [selectedGroup]);

  // normalize schedules
  const schedules = useMemo(
    () =>
      (Array.isArray(schedulesRaw) ? schedulesRaw : []).map((s) => ({
        ...s,
        group_name: s.group_name ?? '',
        subject: s.subject ?? '',
        teacher: s.teacher ?? '',
        room: s.room ?? '',
        time_slot: s.time_slot ?? '',
        date: s.date ?? '',
      })),
    [schedulesRaw]
  );

  // client filters
  const filteredSchedules = useMemo(() => {
    let result = schedules;

// Для WEEK: фільтр по ПОНЕДІЛОК–П’ЯТНИЦЯ обраного тижня (локальні YYYY-MM-DD)
    const inWeek = (dstr) => {
      if (!dstr) return false;
      // dstr з БД у форматі 'YYYY-MM-DD' — лексикографічне порівняння коректне
      return dstr >= weekRange.mondayStr && dstr <= weekRange.fridayStr;
    };


    if (viewMode === ViewModes.BY_TEACHER) {
      const norm = (s) => String(s || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, '');
      if (teacherViewScope === TeacherViewScopes.MONTH && selectedTeacherMonth) {
        result = result.filter((sch) => {
          if (!sch?.date) return false;
          const d = new Date(sch.date);
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          return y === selectedDate.getFullYear() && m === selectedTeacherMonth;
        });
      }
      if (teacherViewScope === TeacherViewScopes.WEEK) {
        result = result.filter((sch) => sch?.date && inWeek(sch.date));
      }
      if (selectedTeacherGroup) {
        result = result.filter((sch) => sch.group_name === selectedTeacherGroup);
      }
      if (effectiveTeacher) {
        const et = norm(effectiveTeacher);
        result = result.filter((sch) => norm(sch.teacher) === et);
      }
    }

    if (viewMode === ViewModes.BY_GROUP && effectiveGroup) {
      result = result.filter((sch) => sch.group_name === effectiveGroup);
    }

    return result;
  }, [
    schedules,
    viewMode,
    teacherViewScope,
    selectedTeacherMonth,
    selectedTeacherGroup,
    selectedDate,
    effectiveGroup,
    effectiveTeacher,
  ]);

  // teacher → groups map
  const groupsByTeacher = useMemo(() => {
    const map = {};
    (teacherAssignments || []).forEach((ta) => {
      if (!ta.teacher || !ta.group_name) return;
      if (!map[ta.teacher]) map[ta.teacher] = new Set();
      map[ta.teacher].add(ta.group_name);
    });
    const obj = {};
    Object.keys(map).forEach((t) => {
      obj[t] = [...map[t]].sort((a, b) => a.localeCompare(b, 'uk', { sensitivity: 'base' }));
    });
    return obj;
  }, [teacherAssignments]);

  // keep stable lists to avoid blink
  const lastStableTeacherSchedulesRef = useRef([]);
  const isTeacherView = viewMode === ViewModes.BY_TEACHER && !!effectiveTeacher;

  const lastStableGroupSchedulesRef = useRef([]);
  const isGroupView = viewMode === ViewModes.BY_GROUP && !!effectiveGroup;

// ✅ "Готовність" дляTeacherView тільки коли справді є вибраний викладач
  // готовність для вкладки викладача — тільки якщо є обраний викладач
  const teacherDataReady =
    isTeacherView &&
    !schedulesLoading &&
    !assignmentsLoading &&
    (filteredSchedules.length === 0 ||
      filteredSchedules.every(
        (s) => (s?.teacher || '').trim() === (effectiveTeacher || '').trim()
      ));

  const groupDataReady =
    !schedulesLoading &&
    !assignmentsLoading &&
    (!isGroupView ||
      filteredSchedules.every(
        (s) => (s?.group_name || '').trim() === (effectiveGroup || '').trim()
      ));

  useEffect(() => {
    if (isTeacherView && teacherDataReady) lastStableTeacherSchedulesRef.current = filteredSchedules;
    if (isGroupView && groupDataReady) lastStableGroupSchedulesRef.current = filteredSchedules;
  }, [isTeacherView, teacherDataReady, isGroupView, groupDataReady, filteredSchedules]);
  

  // у вкладці викладача без вибору — кеш порожній (щоб не показувався старий)
  useEffect(() => {
    if (viewMode === ViewModes.BY_TEACHER && !effectiveTeacher) {
      lastStableTeacherSchedulesRef.current = [];
    }
  }, [viewMode, effectiveTeacher]);
  

  const displaySchedules = isTeacherView
    ? teacherDataReady
      ? filteredSchedules
      : lastStableTeacherSchedulesRef.current
    : isGroupView
    ? groupDataReady
      ? filteredSchedules
      : lastStableGroupSchedulesRef.current
    : filteredSchedules;

  // initial refresh
  useEffect(() => {
    setLocalLoading(true);
    Promise.all([refreshAssignments(), refreshSchedules()]).finally(() => setLocalLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps+  // 🧼 Для UI: якщо нічого не обрано — віддаємо undefined, а не порожній рядок.
  // Для UI: якщо нічого не обрано — повертаємо undefined, тоді селект не автозаповнюється
  const selectedGroupForUI   = (urlSelectedGroup ?? selectedGroup) || undefined;
  const selectedTeacherForUI = (urlSelectedTeacher ?? selectedTeacher) || undefined;


  return {
    viewMode,
    setViewMode,
    handleViewModeChange: setViewMode,

    selectedDate,
    handleDateChange,

    selectedGroup: selectedGroupForUI,
    setSelectedGroup,

    selectedTeacher: selectedTeacherForUI,
    setSelectedTeacher,

    teacherViewScope,
    setTeacherViewScope,

    selectedTeacherGroup,
    setSelectedTeacherGroup,

    selectedTeacherMonth,
    onTeacherMonthChange: setSelectedTeacherMonth,

    allGroups,
    allTeachers,

    teacherGroups:
      effectiveTeacher && groupsByTeacher[effectiveTeacher]
        ? groupsByTeacher[effectiveTeacher]
        : [],

    filteredSchedules: displaySchedules,
    schedulesLoading,
    assignmentsLoading,

    showTools,
    setShowTools,
    clickCount,
    setClickCount,
    toast,
    localLoading,

    lastStableTeacherSchedulesRef,
    lastStableGroupSchedulesRef,
    teacherDataReady,
    groupDataReady,
  };
}
